import HeroSection from "@/components/HeroSection";
import ZoneSection from "@/components/ZoneSection";
import GallerySection from "@/components/GallerySection";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, BuildingOffice, Check, Star, Plus, MapPin } from "@phosphor-icons/react";

const formulas = [
  {
    id: "start",
    name: "PIXORA Start",
    price: 149,
    tagline: "Formule digitale",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "Installation et reprise incluses"
    ],
    note: "Cette formule est 100 % digitale : aucune impression incluse.",
    options: []
  },
  {
    id: "signature",
    name: "PIXORA Signature",
    price: 199,
    tagline: "Le meilleur équilibre",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "50 impressions haute qualité incluses",
      "Installation et reprise incluses"
    ],
    options: [{ name: "50 impressions supp.", price: 39 }],
    popular: true
  },
  {
    id: "prestige",
    name: "PIXORA Prestige",
    price: 279,
    tagline: "Une expérience premium",
    features: [
      "Galerie photos digitale",
      "20 cadres photo personnalisables",
      "Accessoires photo",
      "Accessoires fun",
      "Flash LED professionnel",
      "150 impressions haute qualité incluses",
      "Installation et reprise incluses"
    ],
    options: [
      { name: "Livre d'or", price: 29 }
    ]
  },
  {
    id: "wedding",
    name: "PIXORA Wedding",
    price: 389,
    tagline: "La formule mariage complète",
    features: [
      "Galerie photos digitale",
      "Cadre photo entièrement personnalisé",
      "Flash LED professionnel",
      "350 impressions haute qualité incluses",
      "Livre d'or personnalisé inclus",
      "Props premium spécial mariage",
      "Installation et reprise incluses"
    ],
    options: [],
    highlight: true
  }
];

function PricingCard({ formula }) {
  const isPopular = formula.popular;
  const isHighlight = formula.highlight;
  
  return (
    <div
      className={`relative flex flex-col h-full rounded-2xl transition-all ${
        isPopular 
          ? "bg-[#0A0A0A] border-2 border-[#C9A962] shadow-[0_0_40px_rgba(201,169,98,0.3)] scale-[1.02]" 
          : isHighlight
            ? "bg-[#0A0A0A] border-2 border-[#C9A962]/60"
            : "bg-[#0A0A0A] border border-[#C9A962]/20 hover:border-[#C9A962]/40"
      }`}
    >
      {/* Badge */}
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-sm font-bold px-5 py-2 rounded-full shadow-lg">
            <Star size={16} weight="fill" />
            Populaire
          </div>
        </div>
      )}
      {isHighlight && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-sm font-bold px-5 py-2 rounded-full shadow-lg">
            💍 Mariage
          </div>
        </div>
      )}
      
      <div className="p-8 flex flex-col flex-grow">
        {/* Header */}
        <div className="text-center mb-6 pt-3">
          <h3 className="text-xl font-bold text-white mb-1">{formula.name}</h3>
          {formula.tagline && (
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A962]/80 mb-3">{formula.tagline}</p>
          )}
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-5xl font-extrabold gradient-text">{formula.price}</span>
            <span className="text-2xl gradient-text">€</span>
          </div>
        </div>
        
        {/* Features */}
        <ul className="space-y-3 mb-4 flex-grow">
          {formula.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A88B4A] flex items-center justify-center flex-shrink-0">
                <Check size={12} weight="bold" className="text-black" />
              </div>
              <span className="text-[#B8A88A] text-sm leading-snug break-words">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Special note (e.g. "100% digitale") */}
        {formula.note && (
          <p className="text-xs italic text-[#8A7A5A] mb-4 leading-snug">
            {formula.note}
          </p>
        )}
        
        {/* Options */}
        {formula.options.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-[#000000] border border-[#C9A962]/30">
            <p className="text-xs text-[#C9A962] font-bold mb-3 uppercase tracking-wider">Options disponibles</p>
            <div className="space-y-2">
              {formula.options.map((opt) => (
                <div key={opt.name} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Plus size={14} className="text-[#C9A962] flex-shrink-0" weight="bold" />
                    <span className="text-[#B8A88A] text-sm truncate">{opt.name}</span>
                  </div>
                  <span className="text-white font-bold text-sm flex-shrink-0">+{opt.price}€</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* CTA */}
        <div className="mt-auto">
          <Link to="/reservation">
            <button 
              className={`w-full py-4 rounded-full font-bold transition-all ${
                isPopular
                  ? "bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] text-black text-lg shadow-[0_0_20px_rgba(201,169,98,0.4)] hover:shadow-[0_0_30px_rgba(201,169,98,0.6)]" 
                  : "border-2 border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
              }`}
            >
              Choisir cette formule
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Location Photobooth Bourgoin-Jallieu | Pixora"
        description="Pixora, location de photobooth premium à Bourgoin-Jallieu. Borne photo pour mariages, anniversaires et événements pros en Nord-Isère. Installation incluse."
        path="/"
      />
      <HeroSection />
      
      {/* FORMULES - Section principale */}
      <section className="py-12 bokeh-section" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Nos <span className="gradient-text">Formules</span>
            </h2>
          </motion.div>
          
          {/* Grille des formules - GRANDE */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
            {formulas.map((formula, index) => (
              <motion.div
                key={formula.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <PricingCard formula={formula} />
              </motion.div>
            ))}
          </div>
          
          {/* Info livraison */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-[#8A7A5A] text-sm mb-12"
          >
            <p>Livraison incluse jusqu'à 20 km de Bourgoin-Jallieu • Au-delà : 0,50€/km (x4 trajets)</p>
          </motion.div>
          
          {/* Pro teaser - Plus compact */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="glass rounded-2xl p-6 border border-[#C9A962]/30 max-w-3xl mx-auto"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C9A962]/10 flex items-center justify-center border border-[#C9A962]/30">
                  <BuildingOffice size={24} className="text-[#C9A962]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Professionnel ?</h3>
                  <p className="text-[#B8A88A] text-sm">Tarifs sur mesure pour vos événements corporate</p>
                </div>
              </div>
              <Link to="/tarifs-professionnels">
                <button className="btn-secondary flex items-center gap-2 text-sm px-5 py-2">
                  Voir les tarifs Pro <ArrowRight size={16} />
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      <GallerySection />
      
      <ZoneSection />

      {/* Local SEO teaser — internal link to dedicated Bourgoin-Jallieu page */}
      <section className="py-10" data-testid="local-seo-teaser">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl bg-[#0A0A0A] border border-[#C9A962]/25 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          >
            <div className="flex gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-[#C9A962]/10 flex items-center justify-center border border-[#C9A962]/30">
                <MapPin size={22} className="text-[#C9A962]" weight="fill" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                  Un <span className="gradient-text">photobooth à Bourgoin-Jallieu</span>&nbsp;?
                </h2>
                <p className="text-[#B8A88A] text-sm sm:text-base">
                  Découvrez notre service de location de borne photo dédié à Bourgoin-Jallieu et au Nord-Isère, avec installation et récupération incluses.
                </p>
              </div>
            </div>
            <Link to="/photobooth-bourgoin-jallieu" data-testid="link-bourgoin-page">
              <button className="btn-primary flex items-center gap-2 text-sm px-5 py-3">
                En savoir plus <ArrowRight size={16} />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* CTA final - Compact */}
      <section className="py-16 bokeh-strong">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30">
              <Heart size={16} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm text-[#C9A962]">Prêt à créer des souvenirs ?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Réservez votre <span className="gradient-text">Photobooth</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/reservation"><button className="btn-primary">Réserver maintenant</button></Link>
              <Link to="/contact"><button className="btn-secondary">Demander un devis</button></Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
